import * as Sentry from '@sentry/react';
import { Box, Divider, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import StoreContext from 'contexts/storeContext';
import { useContext, useEffect, useState } from 'react';
import { DashboardBigNumber } from './DashboardBigNumber';
import { CategoryMetrics, StoreMetrics } from 'types/storeMetrics';
import ApiContext from 'contexts/apiContext';
import Spinner from 'common/Spinner';
import { DaysAndInterval, getDatesFromDaysAndInterval, getIntervalFromDaysAndInterval, Interval } from 'types/daysAndInterval';
import ErrorContext from 'contexts/errorContext';
import { GENERIC_SERVER_ERROR } from 'api';
import DashboardDescription from 'components/Dashboard/DashboardDescription';
import DashboardTitle from 'common/DashboardTitle';

// NOTE: category section is fixed to 30 days
const DAYS_AND_INTERVAL: DaysAndInterval = [30, Interval.DAY];

export default function DashboardCategorySection() {
  const { api } = useContext(ApiContext);
  const { store } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);
  const [storeMetrics, setStoreMetrics] = useState<StoreMetrics>();
  const [categoryMetrics, setCategoryMetrics] = useState<CategoryMetrics>();
  const [categoryMetricsLoading, setCategoryMetricsLoading] = useState(true);
  const [storeMetricsLoading, setStoreMetricsLoading] = useState(true);

  useEffect(() => {
    const { startDate, endDate } = getDatesFromDaysAndInterval(DAYS_AND_INTERVAL);
    const interval = getIntervalFromDaysAndInterval(DAYS_AND_INTERVAL);
    api.categoryMetrics
      .list(store.category.id, startDate, endDate)
      .then((res) => setCategoryMetrics(res.data))
      .catch((err) => {
        setError({ message: GENERIC_SERVER_ERROR });
        console.error(err);
        Sentry.captureException(err);
      })
      .finally(() => {
        setCategoryMetricsLoading(false);
      });
    api.storeMetrics
      .list(store.id, startDate, endDate, interval)
      .then((res) => setStoreMetrics(res.data))
      .catch((err) => {
        setError({ message: GENERIC_SERVER_ERROR });
        console.error(err);
        Sentry.captureException(err);
      })
      .finally(() => {
        setStoreMetricsLoading(false);
      });
  }, [api, setError, store]);

  return categoryMetricsLoading || storeMetricsLoading ? (
    <Spinner />
  ) : (
    categoryMetrics && storeMetrics && (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <DashboardTitle sx={{ mt: 1, fontSize: '1.1rem' }} title='Packs más vendidos' />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Posición</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '100%' }}>Pack</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryMetrics.packs.map((pack, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ p: 1.5 }}>{index + 1}</TableCell>
                    <TableCell sx={{ p: 1.5 }}>{pack.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box height='100%' display='flex' flexDirection='column' justifyContent='flex-start' width='fit-content'>
            <DashboardTitle sx={{ mt: 1, fontSize: '1.1rem', textAlign: 'center' }} title='Tiendas con mejor desempeño' />
            <AverageAndTop
              label='Cantidad de pedidos'
              yours={storeMetrics.orders_count.total}
              average={categoryMetrics.orders_count.average}
              top={categoryMetrics.orders_count.top}
              unit='pedidos'
            />
            <Divider sx={{ mt: 1 }} />
            <AverageAndTop
              label='Facturación'
              yours={storeMetrics.orders_total_sum.total}
              average={categoryMetrics.orders_total_sum.average}
              top={categoryMetrics.orders_total_sum.top}
              unit='$'
            />
            <Divider sx={{ mt: 1 }} />
            <AverageAndTop
              label='Ticket promedio'
              yours={storeMetrics.orders_total_avg.total}
              average={categoryMetrics.orders_total_avg.average}
              top={categoryMetrics.orders_total_avg.top}
              unit='$'
            />
            <Divider sx={{ mt: 1 }} />
            <AverageAndTop
              label='Porcentaje vendido'
              yours={storeMetrics.published_sold_percentage.total}
              average={categoryMetrics.published_sold_percentage.average}
              top={categoryMetrics.published_sold_percentage.top}
              unit='%'
            />
          </Box>
        </Grid>
      </Grid>
    )
  );
}

function AverageAndTop({ label, yours, average, top, unit }: { label: string; yours: number; average: number; top: number; unit: string }) {
  return (
    <Box>
      <DashboardDescription sx={{ textAlign: 'center', mt: 2, mb: 1, fontWeight: 600, opacity: 1, fontSize: 14 }}>
        {label}
      </DashboardDescription>
      <Box display='flex' justifyContent='center'>
        <DashboardBigNumber sx={{ fontSize: '1.25rem' }} boxSx={{ minWidth: 0 }} label='Tu tienda' value={yours} unit={unit} />
        <DashboardBigNumber sx={{ fontSize: '1.25rem' }} boxSx={{ minWidth: 0 }} label='Promedio' value={average} unit={unit} />
        <DashboardBigNumber sx={{ fontSize: '1.25rem' }} boxSx={{ minWidth: 0 }} label='Líder' value={top} unit={unit} />
      </Box>
    </Box>
  );
}
